import React, {useEffect, useState} from 'react'
import { Link, useLocation } from 'react-router-dom'
import {FirebaseSocialButtons} from "../../components/FirebaseSocialButtons";
import {GoogleLoginButton} from "../../components/GoogleLoginButton";
import {isFirebaseAuthEnabled, SocialProviderId} from "../../firebase/firebaseClient";
import {AccountConflictError, FirebaseAuthService} from "../../firebase/FirebaseAuthService";
import {RedirectionService} from "../../service/RedirectionService";
import {postAuthorize, postFirebaseAuthorize, postThirdPartyAuthorize} from '../../global/api'
import styles from '../auth/AuthPage.module.scss'

import imgLogo from '../../../assets/images/ic-logo-black.png'

const { REACT_APP_GOOGLE_CLIENT_ID } = process.env

function Login() {

  const location = useLocation()
  const useFirebase = isFirebaseAuthEnabled()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState('')
  const [errorMessage, setErrorMessage] = useState(
    'Please double check that both your email and password are typed correctly.'
  )
  const [hasError, setHasError] = useState(false)
  const [accountConflict, setAccountConflict] = useState<AccountConflictError | null>(null)

  // Finishes a Google/Apple sign-in that fell back from a blocked popup to a full-page redirect.
  useEffect(() => {
    if (!useFirebase) return
    let cancelled = false
    // Keep the form and social buttons disabled until the returning sign-in has been exchanged, so a
    // second sign-in can't race it.
    setIsSubmitting(true)
    FirebaseAuthService.completeRedirectSignIn()
      .then((idToken) => {
        if (idToken && !cancelled) {
          return firebaseAuthorize(idToken)
        }
      })
      .catch((error) => {
        if (!cancelled) handleFirebaseError(error)
      })
      .finally(() => {
        if (!cancelled) setIsSubmitting(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useFirebase])

  const isValidEmail = (value: string) => /^\S+@\S+\.\S+$/.test(value)

  const handleChangePassword = ({ target: { value } }: { target: { value: any } }) => {
    setPassword(value)
    setFieldError('')
  }

  const handleChangeEmail = ({ target: { value } }: { target: { value: any } }) => {
    setEmail(value)
    setFieldError('')
    setHasError(false)
    setAccountConflict(null)
  }

  const getOAuthError = (): string | null => {
    const params = new URLSearchParams(window.location.search)
    if (!params.get('client_id')) return 'Invalid: client_id is missing. Please open the Awair app and try again.'
    if (!params.get('response_type')) return 'Invalid : response_type is missing. Please open the Awair app and try again.'
    return null
  }

  const validateForm = (): boolean => {

    if (!email.trim() || !isValidEmail(email.trim())) {
      setFieldError('Please enter a valid email address.')
      return false
    }

    if (!password) {
      setFieldError('Please enter your password.')
      return false
    }

    return true
  }

  const onSubmit = async (e: any) => {
    e.preventDefault()
    if (isSubmitting) return
    if (!validateForm()) return

    const oauthError = getOAuthError()
    if (oauthError) {
      showError(oauthError)
      return
    }

    setIsSubmitting(true)
    try {
      if (useFirebase) {
        let idToken: string
        if (accountConflict) {
          idToken = await FirebaseAuthService.linkPendingCredential(accountConflict, password)
          // Linked now: a retry after a failed exchange is a plain password sign-in.
          setAccountConflict(null)
        } else {
          idToken = await FirebaseAuthService.signInWithPassword(email, password)
        }
        await firebaseAuthorize(idToken)
      } else {
        const data = await postAuthorize(email, password)
        RedirectionService.redirect(data)
      }
    } catch (error) {
      useFirebase ? handleFirebaseError(error) : showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Exchanges the Firebase ID token for an OAuth2 code, then drops the Firebase session before
  // redirecting so the next OAuth request starts signed out.
  async function firebaseAuthorize(idToken: string) {
    try {
      const data = await postFirebaseAuthorize(idToken)
      await FirebaseAuthService.signOutFirebase()
      RedirectionService.redirect(data)
    } catch (error) {
      await FirebaseAuthService.signOutFirebase()
      throw error
    }
  }

  function handleFirebaseError(error: any) {
    if (error instanceof AccountConflictError) {
      setAccountConflict(error)
      setEmail(error.email)
      setPassword('')
      showError(error.message)
      return
    }
    showError(error instanceof Error ? error.message : error)
  }

  async function onFirebaseSocialSignIn(providerId: SocialProviderId) {
    if (isSubmitting) return
    setHasError(false)
    setIsSubmitting(true)
    try {
      const idToken = await FirebaseAuthService.signInWithProvider(providerId)
      if (idToken) {
        await firebaseAuthorize(idToken)
      }
    } catch (error) {
      handleFirebaseError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const showError = (message: any) => {
    setErrorMessage(message || 'Please try again.')
    setHasError(true)
  }

  async function thirdPartyAuthorize(thirdParty: string, credential: string) {
    try {
      setIsSubmitting(true)
      const data = await postThirdPartyAuthorize(thirdParty, credential)
      RedirectionService.redirect(data)
    } catch (error) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  function onGoogleLoginSuccess(authorizationCode: string) {
    thirdPartyAuthorize('google', authorizationCode)
  }

  function isSmartThingsAuthentication() {
    const redirectUri = new URLSearchParams(window.location.search).get('redirect_uri') || ""
    return redirectUri.includes("api.smartthings")
  }

  const thirdPartyLoginButtonSection = (
    <div className={styles.socialSection}>
      <div className={styles.dividerRow}>
        <div className={styles.divider}></div>
        <p className={styles.dividerText}>or continue with</p>
        <div className={styles.divider}></div>
      </div>
      <div className='flex flex-col justify-content-between'>
        {useFirebase ? (
          <FirebaseSocialButtons disabled={isSubmitting} onSignIn={onFirebaseSocialSignIn}/>
        ) : (
          <GoogleLoginButton clientId={REACT_APP_GOOGLE_CLIENT_ID || ''} onSuccess={onGoogleLoginSuccess} onError={showError}/>
        )}
      </div>
    </div>
  )

  return (
    <main className={styles.authPage}>
      <section className={styles.authShell} aria-label='Awair sign-in'>
        <div className={styles.card}>
          <img className={styles.logo} src={imgLogo} alt='Awair logo' />
          <p className={styles.eyebrow}>Awair Account</p>
          <h1 className={styles.title}>Sign in</h1>
          <p className={styles.subtitle}>Enter your account details to continue.</p>

          <form className={styles.form} onSubmit={onSubmit} noValidate>
            <div>
              <label htmlFor='user-email' className={styles.fieldLabel}>Email address</label>
              <input
                type='email'
                className={`${styles.input} ${fieldError ? styles.inputError : ''}`}
                id='user-email'
                name='user_email'
                placeholder='name@example.com'
                autoComplete='username'
                value={email}
                onChange={handleChangeEmail}
                aria-invalid={Boolean(fieldError)}
              />
            </div>

            <div>
              <label htmlFor='user-password' className={styles.fieldLabel}>Password</label>
              <input
                type='password'
                className={`${styles.input} ${fieldError ? styles.inputError : ''}`}
                id='user-password'
                name='user_pw'
                placeholder='Enter your password'
                autoComplete='current-password'
                value={password}
                onChange={handleChangePassword}
                aria-invalid={Boolean(fieldError)}
              />
            </div>

            <div className={styles.helperRow}>
              <Link to={{ pathname: '/forgot-password', search: location.search }} className={styles.link}>Forgot password?</Link>
            </div>

            <button
              className={styles.submitButton}
              type='submit'
              id='mc-embedded-subscribe'
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </button>

            {fieldError ? (
              <p className={`${styles.status} ${styles.statusError}`} role='alert'>
                {fieldError}
              </p>
            ) : null}

            {hasError ? (
              <p className={`${styles.status} ${styles.statusError}`} role='alert' aria-live='assertive'>
                {errorMessage}
              </p>
            ) : null}
          </form>

          {isSmartThingsAuthentication() ? null : thirdPartyLoginButtonSection}

          <p className={styles.footerNote}>Secure sign-in for your Awair account.</p>
        </div>
      </section>
    </main>
  )
}

export default Login
